import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import {
  Typography,
  Button,
  Container,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  Paper,
  Box,
} from "@mui/material"

import DashboardIcon from "@mui/icons-material/Dashboard"
import BusinessIcon from "@mui/icons-material/Business"
import LanguageIcon from "@mui/icons-material/Language"

// Import dashboard components
import GlobalDashboard from "./globalDashboard"
import SalonDashboard from "./salonDashboard"

// API service for salon information
import { useSalonsMySalonsRetrieveQuery } from "../../../services/GLOWClient"
import { theme } from "../../../theme"
import { useAbility } from "../../../permissions/AbilityContext"

function DashboardContent() {
  const { t } = useTranslation()
  const { data: salons, isLoading: salonsLoading, error: salonsError } = useSalonsMySalonsRetrieveQuery()
  const [viewMode, setViewMode] = useState("global") // 'global' | 'salon'
  const [selectedSalonId, setSelectedSalonId] = useState("")
  const muiTheme = useTheme()
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"))
  const isTablet = useMediaQuery(muiTheme.breakpoints.between("sm", "lg"))
  const isLargeTablet = useMediaQuery(muiTheme.breakpoints.between("md", "xl"))
  const salonsCount = Array.isArray(salons) ? salons.length : 0
  const ability = useAbility()
  const canGlobal = ability?.can && ability.can('read', 'dashboard_global')
  const canSalon = ability?.can && ability.can('read', 'dashboard_salon')
  
  console.log('=== DashboardContent Rendering ===')
  console.log('DashboardContent - salons:', salons)
  console.log('DashboardContent - loading:', salonsLoading)
  console.log('DashboardContent - error:', salonsError)
  console.log('DashboardContent - viewMode:', viewMode)
  console.log('DashboardContent - selectedSalonId:', selectedSalonId)
  console.log('DashboardContent - isTablet:', isTablet)

  // Initialize default selected salon when salons load
  useEffect(() => {
    if (salons && salons.length > 0 && !selectedSalonId) {
      setSelectedSalonId(String(salons[0].salon_id))
    }
  }, [salons, selectedSalonId])

  // Permission-based defaulting and fallback
  useEffect(() => {
    if (!canGlobal && !canSalon) {
      setViewMode('none')
      return
    }
    if (viewMode === 'global' && !canGlobal && canSalon) {
      setViewMode('salon')
      return
    }
    if (viewMode === 'salon' && !canSalon && canGlobal) {
      setViewMode('global')
      return
    }
    if (salonsCount <= 1 && viewMode !== 'global' && canGlobal) {
      setViewMode('global')
    }
  }, [salonsCount, viewMode, canGlobal, canSalon])

  // Handle toggle change
  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode)
    }
  }

  // Handle salon dropdown change
  const handleSalonChange = (event) => {
    const newSalonId = event.target.value
    setSelectedSalonId(newSalonId)
  }
  
  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        px: { xs: 1, sm: 2, md: 3, lg: 4 },
        py: { xs: 1, sm: 2 },
      }}
    >
      {/* Modern Header */}
      <Box sx={{ mb: 4 }}>
        {/* Title Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: isTablet ? 2 : isLargeTablet ? 3 : 2 }, mb: 3 }}>
          <Box
            sx={{
              width: { xs: 42, sm: isTablet ? 48 : isLargeTablet ? 64 : 48 },
              height: { xs: 42, sm: isTablet ? 48 : isLargeTablet ? 64 : 48 },
              borderRadius: 2,
              backgroundColor: theme.colors.primary[50],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DashboardIcon sx={{ color: theme.colors.primary[600], fontSize: { xs: '1.45rem', sm: isTablet ? '1.7rem' : isLargeTablet ? '2.2rem' : '1.75rem' } }} />
          </Box>
          <Box>
            <Typography variant={isLargeTablet ? "h3" : isTablet ? "h5" : "h4"} sx={{ fontWeight: 700, color: theme.colors.text.primary, lineHeight: 1.15 }}>
              {t('pro_dashboard.dashboard.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mt: 0.5, fontSize: { xs: '0.85rem', sm: isTablet ? '0.95rem' : isLargeTablet ? '1.1rem' : '0.875rem' } }}>
              {t('pro_dashboard.dashboard.subtitle')}
            </Typography>
          </Box>
        </Box>

        {/* Controls Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', lg: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', lg: 'row' },
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {/* Toggle Buttons */}
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${theme.colors.primary[200]}`,
              borderRadius: 2,
              p: 0.5,
              display: 'flex',
              flexWrap: 'wrap',
              width: { xs: '100%', lg: 'auto' },
              gap: 0.5,
            }}
          >
            {canGlobal && (
            <Button
              variant={viewMode === 'global' ? 'contained' : 'text'}
              startIcon={<LanguageIcon />}
              onClick={() => handleViewModeChange(null, 'global')}
              sx={{
                borderRadius: 1.5,
                px: { xs: 1.5, sm: isTablet ? 2 : isLargeTablet ? 3.5 : 3 },
                py: { xs: 0.85, sm: 1 },
                textTransform: 'none',
                fontWeight: 600,
                flex: { xs: '1 1 100%', sm: '1 1 auto' },
                minWidth: { sm: isTablet ? 160 : isLargeTablet ? 200 : 'auto' },
                fontSize: { xs: '0.9rem', sm: isTablet ? '0.9rem' : isLargeTablet ? '1rem' : '0.95rem' },
                '& .MuiButton-startIcon svg': {
                  fontSize: { xs: '1rem', sm: isTablet ? '1.05rem' : isLargeTablet ? '1.25rem' : '1.15rem' },
                },
                ...(viewMode === 'global' && {
                  backgroundColor: theme.colors.primary[500],
                  color: 'white',
                  '&:hover': {
                    backgroundColor: theme.colors.primary[600],
                  },
                }),
                ...(viewMode !== 'global' && {
                  color: theme.colors.text.secondary,
                  '&:hover': {
                    backgroundColor: theme.colors.primary[50],
                  },
                }),
              }}
            >
              {t('pro_dashboard.dashboard.global_view')}
            </Button>
            )}
            {salonsCount > 1 && canSalon && (
              <Button
                variant={viewMode === 'salon' ? 'contained' : 'text'}
                startIcon={<BusinessIcon />}
                onClick={() => handleViewModeChange(null, 'salon')}
                disabled={!salons || salons.length === 0}
                sx={{
                  borderRadius: 1.5,
                  px: { xs: 1.5, sm: isTablet ? 2.5 : isLargeTablet ? 4 : 3 },
                  py: { xs: 0.85, sm: 1 },
                  textTransform: 'none',
                  fontWeight: 600,
                  flex: { xs: '1 1 100%', sm: '1 1 auto' },
                  minWidth: { sm: isTablet ? 180 : isLargeTablet ? 240 : 'auto' },
                  fontSize: { xs: '0.9rem', sm: isTablet ? '0.95rem' : isLargeTablet ? '1.1rem' : '0.95rem' },
                  '& .MuiButton-startIcon svg': {
                    fontSize: { xs: '1rem', sm: isTablet ? '1.1rem' : isLargeTablet ? '1.4rem' : '1.15rem' },
                  },
                  ...(viewMode === 'salon' && {
                    backgroundColor: theme.colors.primary[500],
                    color: 'white',
                    '&:hover': {
                      backgroundColor: theme.colors.primary[600],
                    },
                  }),
                  ...(viewMode !== 'salon' && {
                    color: theme.colors.text.secondary,
                    '&:hover': {
                      backgroundColor: theme.colors.primary[50],
                    },
                  }),
                }}
              >
                {t('pro_dashboard.dashboard.by_salon')}
              </Button>
            )}
          </Paper>

          {/* Salon Selector - Visible uniquement en vue "Par salon" */}
          {viewMode === 'salon' && salonsCount > 1 && (
            <Select
              size="small"
              value={selectedSalonId}
              onChange={handleSalonChange}
              displayEmpty
              disabled={!salons || salons.length === 0}
              sx={{
                minWidth: { xs: '100%', sm: isTablet ? 240 : isLargeTablet ? 320 : 250 },
                width: { xs: '100%', lg: 'auto' },
                backgroundColor: 'white',
                border: `1px solid ${theme.colors.primary[200]}`,
                borderRadius: 2,
                fontSize: { xs: '0.9rem', sm: isTablet ? '0.9rem' : '0.95rem' },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
              renderValue={(value) => {
                if (!value) return t('pro_dashboard.dashboard.select_salon')
                const salon = (salons || []).find((s) => String(s.salon_id) === String(value))
                return salon ? salon.name : t('pro_dashboard.dashboard.select_salon')
              }}
            >
              <MenuItem value="" disabled>
                {salons && salons.length > 0 ? t('pro_dashboard.dashboard.select_salon') : t('pro_dashboard.dashboard.no_salon_available')}
              </MenuItem>
              {(salons || []).map((s) => (
                <MenuItem key={s.salon_id} value={String(s.salon_id)}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {s.name}
                    </Typography>
                    {s.formatted_address && (
                      <Typography variant="caption" noWrap sx={{ color: theme.colors.text.secondary }}>
                        {s.formatted_address}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>
      </Box>

      {/* Content routes */}
      <Box
        sx={{
          mt: { xs: 2, sm: 3 },
          px: { xs: 0, sm: 1 },
        }}
      >
        {viewMode === "none" ? (
          <Typography sx={{ color: theme.colors.text.secondary, mt: 2 }}>
            {t('pro_dashboard.dashboard.subscription_required')}
          </Typography>
        ) : viewMode === "global" ? (
          <GlobalDashboard setSelectedSalonId={setSelectedSalonId} setViewMode={setViewMode} />
        ) : (
          <SalonDashboard salonId={selectedSalonId} />
        )}
      </Box>
    </Container>
  )
}

export default DashboardContent
